import React, { useEffect, useRef, useState } from "react";
import {
  PROPERTY_LOAN_PROCESSING_FEE_LABEL,
  propertyLoanProcessingFee,
  propertyLoanNetDisbursement,
} from "../../utils/propertyLoanProcessingFee";
import { InputNumber, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import heroArt from "../../assets/property-loan/property-hero.svg";
import singpassLogo from "../../assets/singpass/singpass-full-colour.png";
import landedArt from "../../assets/property-loan/property-landed.svg";
import condoArt from "../../assets/property-loan/property-condo.svg";
import PropertyLoanNoEquity from "./PropertyLoanNoEquity";
import {
  formatPropertyMoneyInput,
  parsePropertyMoneyInput,
  normalizePropertyMoney,
  sumPropertyMoney,
} from "../../utils/propertyLoanMoney";
import PropertyLoanCoOwners from "./PropertyLoanCoOwners";
import {
  applicantPropertyCpfUsage,
  totalPropertyCpfUsage,
  withPropertyOwnershipDetails,
  isCoOwnedProperty,
  createEmptyPropertyCoOwner,
  propertyOwnershipError,
  propertyOwnershipLabel,
} from "../../utils/propertyLoanOwnership";
import PropertyLoanRepaymentSchedule from "./PropertyLoanRepaymentSchedule";
import { propertyLoanMonthlyRepayment } from "../../utils/propertyLoanRepayments";
import {
  halfCaveatLimit,
  HALF_CAVEAT_LTV,
  propertyLoanTenureOptions,
  propertyLoanLtvLimit,
  propertyLoanMonths,
  propertyLoanMonthlyRate,
  propertyLoanTenureLabel,
} from "../../utils/propertyLoanProducts";

const money = (value) =>
  Number(value || 0).toLocaleString("en-SG", {
    style: "currency",
    currency: "SGD",
    maximumFractionDigits: 2,
  });
const address = (property) => {
  const a = property?.address || {};
  return [
    a.block?.value,
    a.street?.value,
    a.floor?.value && a.unit?.value ? `#${a.floor.value}-${a.unit.value}` : "",
    a.postal?.value,
    a.country?.desc,
  ]
    .filter(Boolean)
    .join(", ");
};
const flow = [
  "Application details",
  "Loan selection",
  "CBS report",
  "Loan submission",
];
const introSteps = [
  ["Retrieve property", "Singpass Myinfo"],
  ["Instant valuation", "See your equity estimate"],
  ["Loan details + CBS", "Amount, tenure, credit report"],
  ["Compare offers", "Pick the best lender"],
];

function MoneyInput({
  id,
  value,
  onChange,
  readOnly = false,
  max,
  className = "",
  required = false,
  invalid = false,
}) {
  return (
    <div
      className={`pl-money ${readOnly ? "pl-money-readonly" : ""} ${className}`}
    >
      <span>S$</span>
      <InputNumber
        id={id}
        controls={false}
        min={0}
        max={max}
        precision={2}
        value={value}
        readOnly={readOnly}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? "pl-ownership-error" : undefined}
        formatter={(amount, info) =>
          amount === "" || amount == null
            ? ""
            : formatPropertyMoneyInput(amount, info).replace(/^\$\s*/, "")
        }
        parser={parsePropertyMoneyInput}
        onChange={(next) => onChange?.(normalizePropertyMoney(next))}
      />
      {readOnly && <span aria-label="Read-only amount">🔒</span>}
    </div>
  );
}

export default function PropertyLoanApplication({
  form,
  setForm,
  step,
  setStep,
  properties,
  myinfoNotification,
  userDetails,
  result,
  loading,
  valuationStatus,
  retryValuation,
  agreed,
  setAgreed,
  selectedLoanType,
  setSelectedLoanType,
  typesOfLoans,
  uploadDocument,
  cbsPromoCode,
  refreshCbsCode,
  cbsFiles,
  cbsFileList,
  beforeCbsUpload,
  removeCbs,
  cbsExtractedData,
  submitting,
  submit,
  updateRepaymentSchedule,
  onTrackRequested,
  trackLoanAmount,
}) {
  const applicationTop = useRef(null);
  const propertyPanel = useRef(null);
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [editCpf, setEditCpf] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [submissionConsent, setSubmissionConsent] = useState(false);
  const [ownershipAttempt, setOwnershipAttempt] = useState(0);
  const update = (values) =>
    setForm((current) =>
      withPropertyOwnershipDetails({ ...current, ...values }),
    );
  const myinfoReady = !!userDetails?.myinfo_data && myinfoNotification === 0;
  const coOwned = isCoOwnedProperty(form.propertyOwnership);
  const applicantCpf = applicantPropertyCpfUsage(form);
  const totalCpf = totalPropertyCpfUsage(form);
  const cpfChanged = applicantCpf !== form.originalcpfUsage;
  const ownershipError = propertyOwnershipError(form);
  const ownersValid = !ownershipError;
  const documentsValid = !cpfChanged || form.documentForCpfUsage.length > 0;
  const canValue =
    myinfoReady &&
    form.property &&
    form.propertyOwnership &&
    documentsValid &&
    agreed &&
    !uploadingDocument &&
    !loading &&
    Number(result?.value) > 0;
  const ltv = propertyLoanLtvLimit(result?.type);
  const lendingValue = Number(result?.value || 0) * ltv;
  const equity = Math.max(
    0,
    Number(result?.value || 0) - form.oustandingLoan - totalCpf,
  );
  const wholePropertyCash = Math.max(
    0,
    lendingValue - form.oustandingLoan - totalCpf,
  );
  const halfLimit = halfCaveatLimit(form, result?.value);
  const cash = Math.max(wholePropertyCash, halfLimit);
  const productLimit = (type) =>
    type === 2
      ? halfLimit
      : Math.max(
          0,
          lendingValue - totalCpf - (type === 0 ? 0 : form.oustandingLoan),
        );
  const productEligible = (type) =>
    type === 2
      ? form.propertyOwnership === "TenancyInCommon" && halfLimit > 0
      : wholePropertyCash > 0;
  const maxLoan = productLimit(selectedLoanType);
  const minLoan = selectedLoanType === 0 ? form.oustandingLoan + 1 : 1;
  const tenureOptions = propertyLoanTenureOptions(selectedLoanType, form);
  useEffect(() => {
    if (selectedLoanType === null || tenureOptions.includes(Number(form.loanTenureMonths))) return;
    setForm((current) => withPropertyOwnershipDetails({
      ...current,
      loanTenureMonths: tenureOptions[tenureOptions.length - 1],
      loanRepaymentSchedule: [],
    }));
  }, [selectedLoanType, tenureOptions, form.loanTenureMonths, setForm]);
  const loanValid =
    selectedLoanType !== null &&
    productEligible(selectedLoanType) &&
    form.loanAmount >= minLoan &&
    form.loanAmount <= maxLoan &&
    !!form.loanPaymentType &&
    tenureOptions.includes(Number(form.loanTenureMonths));
  const monthlyRate = propertyLoanMonthlyRate(selectedLoanType);
  const months = propertyLoanMonths(selectedLoanType, form);
  const estimatedMonthly = propertyLoanMonthlyRepayment(selectedLoanType, form);
  const navigate = (next) => {
    if (submitting) return;
    setStep(next);
    setSubmissionConsent(false);
    requestAnimationFrame(() =>
      applicationTop.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
      }),
    );
  };
  const chooseProperty = (index) => {
    const property = properties[Number(index)];
    if (!property || property === form.property) return;
    const cpf = sumPropertyMoney(
      property.principalwithdrawalamt?.value,
      property.accruedinterestamt?.value,
    );
    update({
      property,
      addressShownData: address(property),
      applicantCpfUsage: cpf,
      cpfUsage: cpf,
      originalcpfUsage: cpf,
      valuation: null,
      propertyType: null,
      validationNeeded: false,
      documentForCpfUsage: [],
      documentForLoanOustanding: [],
      loanAmount: 0,
      propertyOwnership: "",
      propertyShare: null,
      otherOwners: [],
    });
    setAgreed(false);
    setEditCpf(false);
    setOwnershipAttempt(0);
  };
  const setAmount = (amount) => {
    update({
      loanAmount: normalizePropertyMoney(amount),
      loanRepaymentSchedule: [],
    });
    trackLoanAmount(amount);
  };
  const uploadProof = async (event, type) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingDocument(true);
    try {
      await uploadDocument(file, type);
    } catch {
      message.error(
        "Unable to upload the supporting document. Please try again.",
      );
    } finally {
      setUploadingDocument(false);
      event.target.value = "";
    }
  };
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(cbsPromoCode);
      message.success("Reference code copied.");
    } catch {
      message.info("Please select and copy the reference code above.");
    }
  };
  if (step === 4) {
    return (
      <div className="pl-application" ref={applicationTop}>
        <nav className="pl-flow" aria-label="Application progress">
          {flow.map((label, index) => (
            <React.Fragment key={label}>
              {index > 0 && <span className="pl-flow-line" />}
              <div className="pl-flow-complete">
                <i>✓</i>
                <span>{label}</span>
              </div>
            </React.Fragment>
          ))}
        </nav>
        <section className="pl-done-card" aria-labelledby="pl-submitted-title">
          <div className="pl-done-icon" aria-hidden="true">
            ✓
          </div>
          <h2 id="pl-submitted-title">Application submitted</h2>
          <p>
            Your application has been submitted to our support team for review.
            It will remain hidden from the marketplace until support publishes
            it. No co-owner authorisation is required. Track its status in
            Requested Loans.
          </p>
          <button
            type="button"
            className="pl-primary"
            onClick={onTrackRequested}
          >
            Track it in Requested Loans →
          </button>
        </section>
      </div>
    );
  }

  const proof = (type, label, files, key) => (
    <div className="pl-proof" key={type}>
      <label htmlFor={`proof-${type}`}>{label}</label>
      <p className="pl-hint">
        You updated this amount. Attach a statement or screenshot so we can
        verify it.
      </p>
      <input
        id={`proof-${type}`}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        disabled={uploadingDocument}
        onChange={(event) => uploadProof(event, type)}
      />
      {files.map((file, index) => (
        <div className="pl-file" key={`${file.url}-${index}`}>
          <a href={file.url} target="_blank" rel="noreferrer">
            {file.name}
          </a>
          <button
            type="button"
            className="pl-text-button"
            disabled={uploadingDocument}
            onClick={() =>
              update({ [key]: files.filter((_, i) => i !== index) })
            }
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <div className="pl-application" ref={applicationTop}>
      {step === 0 ? (
        <>
          <section className="pl-hero">
            <div className="pl-hero-copy">
              <div className="pl-eyebrow">
                ⚡ Instant valuation · Powered by Singpass Myinfo
              </div>
              <h2>
                How much is my property worth?
                <br />
                <span>How much can I cash out from it?</span>
              </h2>
              <p>
                Retrieve your private property with Singpass and get an instant
                valuation. We'll show you an estimate of how much you could
                borrow against it, before you share a single document.
              </p>
              <div className="pl-cta-row">
                <button
                  type="button"
                  className="pl-singpass-button"
                  onClick={() => {
                    if (!myinfoReady) {
                      window.location.href = "/stg-b-profile";
                      return;
                    }
                    setShowPropertyForm(true);
                    requestAnimationFrame(() =>
                      propertyPanel.current?.scrollIntoView?.({
                        behavior: "smooth",
                        block: "start",
                      }),
                    );
                  }}
                >
                  <img
                    className="pl-singpass-logo"
                    src={singpassLogo}
                    alt="Singpass"
                    width="80"
                    height="24"
                  />{" "}
                  Check my property's value
                </button>
                <small>Free · No credit check · Takes about 60 seconds</small>
              </div>
              <div className="pl-trust">
                <span>
                  <i aria-hidden="true">✓</i> Singpass Myinfo retrieval
                </span>
                <span>
                  <i aria-hidden="true">✓</i> Offers from multiple lenders
                </span>
                <span>
                  <i aria-hidden="true">✓</i> Free to apply
                </span>
              </div>
            </div>
            <div className="pl-hero-art" aria-hidden="true">
              <img src={heroArt} alt="" />
              <div className="pl-value-pin">
                <small>Estimated value</small>
                <b>S$3,200,000</b>
              </div>
              <div className="pl-value-pin pl-value-pin-gold">
                <small>You could cash out</small>
                <b>up to S$1.9M</b>
              </div>
              <span>Illustration only · landed home, District 15</span>
            </div>
          </section>
          <div className="pl-intro-steps">
            {introSteps.map(([title, subtitle], index) => (
              <div key={title} className={index === 0 ? "is-current" : ""}>
                <b>{index + 1}</b>
                <div>
                  <h4>{title}</h4>
                  <p>{subtitle}</p>
                </div>
              </div>
            ))}
          </div>
          {(!myinfoReady || showPropertyForm || form.property) && (
            <section className="pl-panel" ref={propertyPanel}>
              {!myinfoReady ? (
                <div className="pl-empty">
                  <h3>
                    {myinfoNotification === 2
                      ? "Your Myinfo data is outdated"
                      : "Retrieve your property with Singpass"}
                  </h3>
                  <p>
                    Sync Myinfo from your profile to securely retrieve your
                    property records and CPF housing usage.
                  </p>
                  <a className="pl-primary" href="/stg-b-profile">
                    Sync with Singpass Myinfo →
                  </a>
                </div>
              ) : (
                <>
                  <span className="pl-verified">
                    ✓ Retrieved from Singpass Myinfo
                  </span>
                  <h3>Check your property details</h3>
                  <p className="pl-sub">
                    Confirm the details below to see your valuation and how much
                    you could borrow.
                  </p>
                  <div className="pl-form">
                    <div>
                      <div className="pl-field-label">
                        <label htmlFor="pl-property">
                          Select your property
                        </label>
                        <span className="pl-source pl-source-with-logo">
                          ✓ From{" "}
                          <img
                            className="pl-singpass-logo"
                            src={singpassLogo}
                            alt="Singpass"
                            width="80"
                            height="24"
                          />{" "}
                          Myinfo
                        </span>
                      </div>
                      <select
                        id="pl-property"
                        value={
                          form.property ? properties.indexOf(form.property) : ""
                        }
                        disabled={!properties.length}
                        onChange={(event) => chooseProperty(event.target.value)}
                      >
                        <option value="" disabled>
                          Select a property
                        </option>
                        {properties.map((property, index) => (
                          <option key={index} value={index}>
                            {address(property)}
                          </option>
                        ))}
                      </select>
                      <p className="pl-hint">
                        Properties available in your synced Myinfo records are
                        listed here. Have a commercial or industrial property?{" "}
                        <a href="/contactus">Contact us</a>.
                      </p>
                      {!properties.length && (
                        <div className="pl-notice" role="status">
                          <b>No usable property found</b>
                          <p>
                            There is currently no property with a usable postal
                            code in your synced Myinfo records.
                          </p>
                          <a href="/stg-b-profile">Resync Myinfo</a> ·{" "}
                          <a href="/contactus">
                            Property not listed? Request an assessment
                          </a>
                        </div>
                      )}
                    </div>
                    {form.property && (
                      <>
                        <div>
                          <div className="pl-field-label">
                            <label htmlFor="pl-mortgage">
                              Outstanding mortgage
                            </label>
                            <span className="pl-you">You enter</span>
                          </div>
                          <MoneyInput
                            id="pl-mortgage"
                            value={form.oustandingLoan}
                            onChange={(value) => {
                              update({
                                oustandingLoan: value,
                              });
                              setAgreed(false);
                            }}
                          />
                          <p className="pl-hint">
                            Enter the current outstanding balance. Enter 0 if
                            your property is fully paid.
                          </p>
                        </div>
                        <div>
                          <div className="pl-field-label">
                            <label htmlFor="pl-ownership">
                              Property ownership
                            </label>
                            <span className="pl-you">You confirm</span>
                          </div>
                          <select
                            id="pl-ownership"
                            value={form.propertyOwnership}
                            onChange={(event) => {
                              const ownership = event.target.value;
                              update({
                                propertyOwnership: ownership,
                                propertyShare:
                                  ownership === "TenancyInCommon"
                                    ? (form.propertyShare ?? 50)
                                    : null,
                                otherOwners: isCoOwnedProperty(ownership)
                                  ? form.otherOwners.length
                                    ? form.otherOwners
                                    : [createEmptyPropertyCoOwner()]
                                  : [],
                              });
                              setAgreed(false);
                            }}
                          >
                            <option value="" disabled>
                              Select an ownership type
                            </option>
                            <option value="Single">Single owner</option>
                            <option value="JointTenancy">
                              Joint tenancy (all owners hold the whole property)
                            </option>
                            <option value="TenancyInCommon">
                              Tenancy-in-common (each owner holds a share)
                            </option>
                          </select>
                        </div>
                        {form.propertyOwnership === "TenancyInCommon" && (
                          <div>
                            <div className="pl-field-label">
                              <label htmlFor="pl-property-share">
                                <span className="pl-required-label">
                                  Your share of the property
                                </span>
                              </label>
                            </div>
                            <div className="pl-money pl-property-share">
                              <InputNumber
                                key={ownershipAttempt}
                                aria-required="true"
                                aria-invalid={
                                  (ownershipAttempt > 0 &&
                                    !(
                                      Number(form.propertyShare) > 0 &&
                                      Number(form.propertyShare) <= 100
                                    )) ||
                                  undefined
                                }
                                aria-describedby={
                                  ownershipAttempt > 0 && ownershipError
                                    ? "pl-ownership-error"
                                    : undefined
                                }
                                className={
                                  ownershipAttempt > 0 &&
                                  !(
                                    Number(form.propertyShare) > 0 &&
                                    Number(form.propertyShare) <= 100
                                  )
                                    ? "pl-field-invalid"
                                    : ""
                                }
                                id="pl-property-share"
                                controls={false}
                                min={0.01}
                                max={100}
                                precision={2}
                                value={form.propertyShare}
                                onChange={(value) => {
                                  update({ propertyShare: value });
                                  setAgreed(false);
                                }}
                              />
                              <span>%</span>
                            </div>
                            <p className="pl-hint">
                              Enter your registered ownership share. It's shown
                              on your title deed or an SLA title search.
                            </p>
                          </div>
                        )}

                        <div>
                          <div className="pl-field-label">
                            <label htmlFor="pl-cpf">
                              CPF Housing Withdrawal Usage
                            </label>
                            <span
                              className={`${editCpf || coOwned ? "pl-you" : "pl-source"} pl-source-with-logo`}
                            >
                              {coOwned ? (
                                <>
                                  ✎{" "}
                                  <img
                                    className="pl-singpass-logo"
                                    src={singpassLogo}
                                    alt="Singpass"
                                    width="80"
                                    height="24"
                                  />{" "}
                                  + co-owner keyed in
                                </>
                              ) : editCpf ? (
                                "You updated"
                              ) : (
                                <>
                                  ✓ From{" "}
                                  <img
                                    className="pl-singpass-logo"
                                    src={singpassLogo}
                                    alt="Singpass"
                                    width="80"
                                    height="24"
                                  />{" "}
                                  Myinfo
                                </>
                              )}
                            </span>
                          </div>
                          <MoneyInput
                            id="pl-cpf"
                            className={coOwned ? "pl-cpf-joint" : ""}
                            value={coOwned ? totalCpf : applicantCpf}
                            readOnly={coOwned || !editCpf}
                            onChange={(value) => {
                              update({
                                applicantCpfUsage: value,
                                documentForCpfUsage: [],
                              });
                              setAgreed(false);
                            }}
                          />
                          <p className="pl-hint">
                            {coOwned
                              ? "Total for all owners: your figure from Singpass plus each co-owner's figure below. It is refunded to CPF when the property is sold, so lenders deduct it from your equity."
                              : "Your CPF housing usage plus accrued interest. It is refunded to CPF when the property is sold, so lenders deduct it from your equity."}{" "}
                            <button
                              type="button"
                              className="pl-text-button"
                              onClick={() => setEditCpf(!editCpf)}
                            >
                              {editCpf ? "Lock amount" : "Update this amount"}
                            </button>
                          </p>
                        </div>
                        {coOwned && (
                          <PropertyLoanCoOwners
                            validationAttempt={ownershipAttempt}
                            owners={form.otherOwners}
                            applicantCpf={applicantCpf}
                            totalCpf={totalCpf}
                            mainCpfChanged={cpfChanged}
                            editCpf={editCpf}
                            onApplicantCpfChange={(value) => {
                              update({
                                applicantCpfUsage: value,
                                documentForCpfUsage: [],
                              });
                              setAgreed(false);
                            }}
                            onChange={(otherOwners) => {
                              update({ otherOwners });
                              setAgreed(false);
                            }}
                            onSingleOwner={() => {
                              update({
                                propertyOwnership: "Single",
                                otherOwners: [],
                              });
                              setAgreed(false);
                            }}
                            MoneyInput={MoneyInput}
                            formatMoney={money}
                          />
                        )}
                        {cpfChanged &&
                          proof(
                            "cpf",
                            "CPF housing usage statement",
                            form.documentForCpfUsage,
                            "documentForCpfUsage",
                          )}
                        {uploadingDocument && (
                          <p role="status">Uploading supporting document…</p>
                        )}
                        {loading && (
                          <div className="pl-loading" role="status">
                            <span /> Retrieving your property's estimated
                            valuation…
                          </div>
                        )}
                        {!loading && !Number(result?.value) && (
                          <div className="pl-notice" role="status">
                            <b>
                              {valuationStatus === "unsupported_hdb"
                                ? "HDB properties are not eligible"
                                : valuationStatus === "failed"
                                  ? "We couldn't retrieve your valuation"
                                  : "No automatic valuation found"}
                            </b>
                            <p>
                              {valuationStatus === "unsupported_hdb"
                                ? "HDB properties cannot be used for property-backed loans. Please select a landed property, condo, or office to continue."
                                : "Try again or contact a Relationship Manager for help arranging a manual assessment."}
                            </p>
                            {valuationStatus !== "unsupported_hdb" && (
                              <>
                                <button
                                  type="button"
                                  className="pl-secondary"
                                  onClick={retryValuation}
                                >
                                  Retry valuation
                                </button>{" "}
                                <a href="/contactus">
                                  Contact a Relationship Manager
                                </a>
                              </>
                            )}
                          </div>
                        )}
                        <label className="pl-confirm">
                          <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(event) =>
                              setAgreed(event.target.checked)
                            }
                          />
                          I confirm that the information above is up to date and
                          correct, and I consent to SmartLend using my Singpass
                          Myinfo data for this valuation.
                        </label>
                        <button
                          type="button"
                          className="pl-primary"
                          disabled={!canValue}
                          onClick={() => {
                            if (!ownersValid) {
                              if (coOwned && !form.otherOwners.length)
                                update({
                                  otherOwners: [createEmptyPropertyCoOwner()],
                                });
                              setOwnershipAttempt((attempt) => attempt + 1);
                              requestAnimationFrame(() => {
                                const field =
                                  applicationTop.current?.querySelector(
                                    '[aria-invalid="true"]',
                                  );
                                field?.focus();
                                field?.scrollIntoView?.({
                                  behavior: "smooth",
                                  block: "center",
                                });
                              });
                              return;
                            }
                            update({
                              valuation: lendingValue,
                              estimatedPropertyValue: Number(result.value),
                              propertyType: result.type,
                              validationNeeded: cpfChanged,
                            });
                            if (
                              selectedLoanType === null ||
                              !productEligible(selectedLoanType)
                            ) {
                              setSelectedLoanType(
                                wholePropertyCash > 0
                                  ? 0
                                  : halfLimit > 0
                                    ? 2
                                    : null,
                              );
                              update({
                                loanAmount: 0,
                                loanRepaymentSchedule: [],
                              });
                            } else if (form.loanAmount > maxLoan) {
                              update({
                                loanAmount: 0,
                                loanRepaymentSchedule: [],
                              });
                            }
                            navigate(1);
                          }}
                        >
                          Show my valuation & how much I can borrow →
                        </button>
                        {ownershipAttempt > 0 && ownershipError && (
                          <p
                            id="pl-ownership-error"
                            className="pl-required-error"
                            role="alert"
                          >
                            {ownershipError}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </section>
          )}
        </>
      ) : (
        <>
          <nav className="pl-flow" aria-label="Application progress">
            {flow.map((label, index) => (
              <React.Fragment key={label}>
                {index > 0 && <span className="pl-flow-line" />}
                <button
                  type="button"
                  disabled={index > step || submitting}
                  aria-current={index === step ? "step" : undefined}
                  className={`${index < step ? "is-done" : ""} ${index === step ? "is-current" : ""}`}
                  onClick={() => navigate(index)}
                >
                  <i>{index < step ? "✓" : index + 1}</i>
                  <span>{label}</span>
                </button>
              </React.Fragment>
            ))}
          </nav>
          <div className="pl-stepbar">
            <button
              type="button"
              className="pl-back"
              onClick={() => navigate(Math.max(0, step - 1))}
            >
              ←{" "}
              {step === 1
                ? "Back to property details"
                : step === 2
                  ? "Back to loan selection"
                  : "Back to CBS report"}
            </button>
            <span>Step {step + 1} of 4</span>
          </div>
          {step === 1 && (
            <>
              {cash > 0 ? (
                <>
                  <section className="pl-eligibility">
                    <div className="pl-eligibility-copy">
                      <span className="pl-eyebrow">
                        🎉 Congratulations, you're eligible!
                      </span>
                      <p className="pl-eligibility-kicker">
                        You could cash out up to
                      </p>
                      <div className="pl-eligibility-amount">
                        {money(cash)}
                        <sup>*</sup>
                      </div>
                      <p className="pl-eligibility-sub">
                        Put the value in your property to work. Explore the
                        options below.
                      </p>
                      <div className="pl-eligibility-chips">
                        <div>
                          <small>Estimated valuation</small>
                          <b>{money(result.value)}</b>
                        </div>
                        <div>
                          <small>Your equity</small>
                          <b>{money(equity)}</b>
                        </div>
                        <div>
                          <small>Max loan-to-value</small>
                          <b>
                            up to{" "}
                            {Math.max(
                              ltv,
                              form.propertyOwnership === "TenancyInCommon"
                                ? HALF_CAVEAT_LTV
                                : 0,
                            ) * 100}
                            %
                          </b>
                        </div>
                      </div>
                    </div>
                    <div className="pl-result-art" aria-hidden="true">
                      <img
                        src={result.type === "land" ? landedArt : condoArt}
                        alt=""
                      />
                      <div className="pl-value-pin">
                        <small>Estimated property valuation</small>
                        <b>{money(result.value)}</b>
                        <small>Indicative estimate</small>
                      </div>
                      <span>✓ Property retrieved from Myinfo</span>
                    </div>
                    <p className="pl-eligibility-foot">
                      *Indicative estimate only, not a loan offer. Based on your
                      property's estimated value, less your outstanding mortgage
                      and CPF housing usage. Half Caveat estimates use your
                      ownership share of the value and mortgage, and your own
                      CPF usage only. Final amounts depend on the lender's
                      valuation, credit assessment and approval.
                    </p>
                  </section>
                </>
              ) : (
                <PropertyLoanNoEquity
                  value={Number(result.value)}
                  mortgage={form.oustandingLoan}
                  cpf={totalCpf}
                  ltv={ltv}
                  onEdit={() => navigate(0)}
                  formatMoney={money}
                />
              )}
              {cash > 0 && (
                <details className="pl-calculation">
                  <summary>How did we work this out?</summary>
                  <div>
                    {[
                      ["Estimated valuation", result.value],
                      [
                        `Maximum lending at up to ${ltv * 100}% LTV`,
                        lendingValue,
                      ],
                      ["Less: outstanding mortgage", -form.oustandingLoan],
                      ["Less: CPF housing usage", -totalCpf],
                      [
                        "Cash you could unlock against the whole property*",
                        wholePropertyCash,
                      ],
                      ...(form.propertyOwnership === "TenancyInCommon"
                        ? [
                            [
                              `Your ${form.propertyShare}% share of the property value`,
                              (result.value * form.propertyShare) / 100,
                            ],
                            [
                              `Your share at ${HALF_CAVEAT_LTV * 100}% LTV`,
                              ((result.value * form.propertyShare) / 100) *
                                HALF_CAVEAT_LTV,
                            ],
                            [
                              "Less: your share of the outstanding mortgage",
                              (-form.oustandingLoan * form.propertyShare) / 100,
                            ],
                            [
                              "Less: your CPF housing usage only",
                              -applicantCpf,
                            ],
                            ["Half Caveat borrowing limit*", halfLimit],
                          ]
                        : []),
                    ].map(([label, amount]) => (
                      <div className="pl-calculation-row" key={label}>
                        <span>{label}</span>
                        <b>{money(amount)}</b>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {cash > 0 && (
                <>
                  <h3 className="pl-selection-heading">
                    Choose the loan you'd like to proceed with
                  </h3>
                  <div
                    className={`pl-products ${form.propertyOwnership === "TenancyInCommon" ? "pl-products-three" : ""}`}
                  >
                    {typesOfLoans.map(
                      (product, index) =>
                        (index !== 2 ||
                          form.propertyOwnership === "TenancyInCommon") && (
                          <label
                            key={product.loanName}
                            className={`pl-product ${selectedLoanType === index ? "is-selected" : ""} ${!productEligible(index) ? "is-disabled" : ""}`}
                          >
                            <input
                              type="radio"
                              name="property-loan-product"
                              disabled={!productEligible(index)}
                              checked={selectedLoanType === index}
                              onChange={() => {
                                setSelectedLoanType(index);
                                update({
                                  loanAmount: 0,
                                  selectedLoanType: index,
                                  loanTenureMonths: propertyLoanTenureOptions(index, form).slice(-1)[0],
                                  loanRepaymentSchedule: [],
                                });
                              }}
                            />
                            <div className="pl-product-top">
                              <span className="pl-product-icon">
                                {index === 0 ? "🏠" : index === 2 ? "🔑" : "🏛"}
                              </span>
                              <div>
                                <h4>
                                  {product.loanName}
                                  {index === 1 && <small> (Caveat Loan)</small>}
                                </h4>
                                <span
                                  className={
                                    index === 0
                                      ? "pl-recommended"
                                      : "pl-alternative"
                                  }
                                >
                                  {index === 0
                                    ? "Recommended · lowest rates"
                                    : index === 2
                                      ? "Tenancy-in-common · your share only"
                                      : "Keep your bank loan"}
                                </span>
                              </div>
                              <span className="pl-radio" />
                            </div>
                            <div className="pl-product-amount">
                              <small>Eligible up to</small>
                              <b>
                                {productEligible(index)
                                  ? money(productLimit(index))
                                  : "Not eligible"}
                              </b>
                              <span>
                                {index === 0
                                  ? "Includes refinancing your existing mortgage"
                                  : index === 2
                                    ? `Based on your ${form.propertyShare}% share. Your co-owners’ share isn’t affected.`
                                    : "No need to refinance your bank loan"}
                              </span>
                            </div>
                            {!productEligible(index) && (
                              <p className="pl-hint">
                                No borrowing capacity based on these details.
                              </p>
                            )}
                            <ul>
                              {product.sub.map((item) => (
                                <li key={item.value}>{item.value}</li>
                              ))}
                            </ul>
                          </label>
                        ),
                    )}
                  </div>
                  <section className="pl-panel pl-amount-panel">
                    <div className="pl-amount-grid">
                      <div>
                        <div className="pl-field-label">
                          <label htmlFor="pl-loan-amount">
                            How much would you like to borrow?
                          </label>
                          {selectedLoanType !== null && (
                            <span className="pl-you">Max {money(maxLoan)}</span>
                          )}
                        </div>
                        <MoneyInput
                          id="pl-loan-amount"
                          value={form.loanAmount}
                          onChange={setAmount}
                          max={selectedLoanType === null ? undefined : maxLoan}
                        />
                        <input
                          type="range"
                          aria-label="Loan amount"
                          min={0}
                          max={maxLoan}
                          step={1000}
                          disabled={selectedLoanType === null}
                          value={Math.min(form.loanAmount, maxLoan)}
                          onChange={(event) =>
                            setAmount(Number(event.target.value))
                          }
                          style={{
                            "--pl-range": `${maxLoan ? (form.loanAmount / maxLoan) * 100 : 0}%`,
                          }}
                        />
                        <div className="pl-quick">
                          {[
                            0.25,
                            0.5,
                            0.75,
                            selectedLoanType === 2 ? 1 : ltv,
                          ].map((percent) => (
                            <button
                              type="button"
                              key={percent}
                              disabled={selectedLoanType === null}
                              onClick={() =>
                                setAmount(
                                  Math.floor(
                                    selectedLoanType === 2
                                      ? maxLoan * percent
                                      : Math.max(
                                          0,
                                          Number(result?.value || 0) * percent -
                                            totalCpf -
                                            (selectedLoanType === 0
                                              ? 0
                                              : form.oustandingLoan),
                                        ),
                                  ),
                                )
                              }
                            >
                              {percent === 1 ? "Max" : `${percent * 100}%`}
                            </button>
                          ))}
                        </div>
                        {selectedLoanType !== 2 && (
                          <p className="pl-hint">
                            Percentages use the property value, with CPF usage
                            and any applicable outstanding mortgage deducted.
                          </p>
                        )}
                        <p className="pl-hint">
                          {selectedLoanType === 0
                            ? "Your loan refinances the outstanding mortgage. Your cash payout is reduced by that balance and lender fees."
                            : "Select a loan type, then choose the amount you need."}
                        </p>
                      </div>
                      <div>
                        <label htmlFor="pl-payment">Loan payment type</label>
                        <select
                          id="pl-payment"
                          value={form.loanPaymentType}
                          onChange={(event) => {
                            const loanPaymentType = event.target.value;
                            const options = propertyLoanTenureOptions(selectedLoanType, { loanPaymentType });
                            update({
                              loanPaymentType,
                              loanTenureMonths: options.includes(Number(form.loanTenureMonths))
                                ? Number(form.loanTenureMonths)
                                : options[options.length - 1],
                              loanRepaymentSchedule: [],
                            });
                          }}
                        >
                          <option value="" disabled>
                            Select a payment type
                          </option>
                          <option value="interest">
                            Loan interest, principal at maturity
                          </option>
                          <option value="normal">Principal + interest</option>
                        </select>
                        <p className="pl-hint">
                          {form.loanPaymentType === "interest"
                            ? "Pay interest each month. The full principal is due at maturity."
                            : form.loanPaymentType === "normal"
                              ? "Your monthly instalment includes principal and interest."
                              : "Choose how you would like to repay your loan."}
                        </p>
                        <label htmlFor="pl-tenure">Loan tenure</label>
                        <select
                          id="pl-tenure"
                          value={selectedLoanType === null ? "" : months}
                          disabled={selectedLoanType === null}
                          onChange={(event) =>
                            update({
                              loanTenureMonths: Number(event.target.value),
                              loanRepaymentSchedule: [],
                            })
                          }
                        >
                          <option value="">Choose a loan type first</option>
                          {tenureOptions.map((term) => (
                            <option key={term} value={term}>
                              {selectedLoanType === 0 && form.loanPaymentType !== "interest"
                                ? `${term / 12} ${term === 12 ? "year" : "years"}`
                                : `${term} months${selectedLoanType === 2 ? " · renewable" : ""}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="pl-estimate">
                      <div>
                        <small>Estimated monthly repayment</small>
                        <b>{loanValid ? money(estimatedMonthly) : "—"}</b>
                        <span>
                          {selectedLoanType !== null
                            ? `Based on the existing indicative rate of ${(monthlyRate * 100).toFixed(2)}% per month`
                            : "Choose a loan to see your estimate"}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="pl-primary"
                        disabled={!loanValid}
                        onClick={() => {
                          updateRepaymentSchedule(selectedLoanType);
                          navigate(2);
                        }}
                      >
                        Proceed to CBS report →
                      </button>
                    </div>
                    <p className="pl-disc">
                      Free to apply · No commitment until you accept an offer ·
                      Lenders compete for your loan. Figures are indicative;
                      final terms depend on each lender's valuation and
                      assessment.
                    </p>
                  </section>
                </>
              )}
            </>
          )}
          {step === 2 && (
            <section className="pl-panel pl-cbs">
              <div className="pl-cbs-head">
                <span className="pl-product-icon">📄</span>
                <div>
                  <h3>Attach your Credit Bureau Singapore (CBS) report</h3>
                  <p>
                    Lenders need it to review your credit history. It's{" "}
                    <b>free</b> with your SmartLend reference code and takes
                    about 5 minutes.
                  </p>
                </div>
              </div>
              <div className="pl-reference">
                <div>
                  <small>Your SmartLend reference code</small>
                  <code>{cbsPromoCode || "Reference code unavailable"}</code>
                </div>
                <button
                  type="button"
                  className="pl-secondary"
                  disabled={!cbsPromoCode}
                  onClick={copyCode}
                >
                  Copy code
                </button>
                {!cbsPromoCode && (
                  <button
                    type="button"
                    className="pl-text-button"
                    onClick={refreshCbsCode}
                  >
                    Retry
                  </button>
                )}
                <span>You'll need this in step 2</span>
              </div>
              <details className="pl-walkthrough">
                <summary>Show me how, step by step</summary>
                <div className="pl-walkthrough-grid">
                  {[
                    [
                      "Singpass",
                      "Log in with Singpass",
                      "Open SmartLend's CBS page and scan with your Singpass app.",
                    ],
                    [
                      "Reference code",
                      cbsPromoCode || "Your reference code",
                      "Enter your reference code to get your report free.",
                    ],
                    [
                      "My Credit Report",
                      "Download PDF",
                      "Open your Credit Report and download it as a PDF.",
                    ],
                    [
                      "SmartLend",
                      "Upload CBS report",
                      "Come back to this page and upload your report below.",
                    ],
                  ].map(([title, content, text], index) => (
                    <div key={title}>
                      <div className="pl-walk-screen">
                        <div>
                          ● ● ● <span>{title}</span>
                        </div>
                        <div className="pl-walk-body">
                          {index === 0 && (
                            <>
                              <div className="pl-walk-qr" aria-hidden="true" />
                              <b>{content}</b>
                              <small>Scan with your Singpass app</small>
                            </>
                          )}
                          {index === 1 && (
                            <>
                              <span className="pl-fake-input">{content}</span>
                              <span className="pl-fake-button">Apply</span>
                              <small>Price: S$0.00</small>
                            </>
                          )}
                          {index === 2 && (
                            <>
                              <span className="pl-fake-document">PDF</span>
                              <span className="pl-fake-button">
                                Download PDF
                              </span>
                            </>
                          )}
                          {index === 3 && (
                            <>
                              <span className="pl-fake-upload">⬆</span>
                              <b>{content}</b>
                              <small>Come back to this page</small>
                            </>
                          )}
                        </div>
                      </div>
                      <p>
                        <b>{index + 1}.</b> {text}
                      </p>
                    </div>
                  ))}
                  <p className="pl-walk-tip">
                    Tip: open the CBS page in a new tab so you can return to
                    this page easily. Your progress here is saved.
                  </p>
                </div>
              </details>
              <ol className="pl-cbs-steps">
                <li>
                  <span>1</span>
                  <div>
                    <h4>Open SmartLend's CBS page</h4>
                    <p>
                      Use the link below and log in with Singpass. Keep this
                      page open so you can return.
                    </p>
                    <a
                      className="pl-primary"
                      href="https://www.creditbureau.com.sg/SmartLend_Financial.html"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open CBS page ↗
                    </a>
                  </div>
                </li>
                <li>
                  <span>2</span>
                  <div>
                    <h4>Enter your SmartLend reference code</h4>
                    <p>
                      Apply the code <b>{cbsPromoCode || "shown above"}</b> on
                      the CBS page to get your report for free.
                    </p>
                    <button
                      type="button"
                      className="pl-secondary"
                      disabled={!cbsPromoCode}
                      onClick={copyCode}
                    >
                      Copy code again
                    </button>
                  </div>
                </li>
                <li>
                  <span>3</span>
                  <div>
                    <h4>Download your credit report as a PDF</h4>
                    <p>
                      Save the PDF to your device. Use a report dated within the
                      last 30 days.
                    </p>
                  </div>
                </li>
                <li>
                  <span>{cbsFiles.length ? "✓" : "4"}</span>
                  <div>
                    <h4>Upload your CBS report here</h4>
                    <p>Your report will be attached to your application.</p>
                    <Upload
                      id="pl-cbs-report"
                      aria-label="CBS report PDF"
                      accept=".pdf,application/pdf"
                      fileList={cbsFileList}
                      beforeUpload={(file) => {
                        if (!/\.pdf$/i.test(file.name)) {
                          message.warning("Please choose a PDF report.");
                          return Upload.LIST_IGNORE;
                        }
                        return beforeCbsUpload(file);
                      }}
                      onRemove={removeCbs}
                      maxCount={1}
                    >
                      <button type="button" className="pl-drop">
                        <UploadOutlined />
                        <b>
                          {cbsFiles.length
                            ? "Replace CBS report"
                            : "Click to upload your CBS report"}
                        </b>
                        <span>PDF document</span>
                      </button>
                    </Upload>
                  </div>
                </li>
              </ol>
              {cbsExtractedData && (
                <div className="pl-cbs-summary">
                  <b>CBS summary</b>
                  <span>
                    Credit score: {cbsExtractedData.credit_score || "—"}
                  </span>
                  <span>Risk grade: {cbsExtractedData.risk_grade || "—"}</span>
                  <span>
                    Probability of default:{" "}
                    {cbsExtractedData.probability_default || "—"}
                  </span>
                </div>
              )}
              <div className="pl-actions">
                <button
                  type="button"
                  className="pl-back"
                  onClick={() => navigate(1)}
                >
                  ← Back to loan selection
                </button>
                <button
                  type="button"
                  className="pl-primary"
                  disabled={!cbsFiles.length}
                  onClick={() => navigate(3)}
                >
                  Proceed to review →
                </button>
              </div>
            </section>
          )}
          {step === 3 && (
            <section
              className="pl-panel pl-review"
              aria-label="Review loan application"
            >
              <div className="pl-review-head">
                <div>
                  <h3>Review and submit</h3>
                  <p>
                    Check your loan details before we send them to our lending
                    partners.
                  </p>
                </div>
                <span className="pl-you">
                  {typesOfLoans[selectedLoanType]?.loanName}
                </span>
              </div>
              <div className="pl-review-summary">
                <div className="pl-summary-hero">
                  <small>Loan amount</small>
                  <b>{money(form.loanAmount)}</b>
                  <span>
                    {form.loanPaymentType === "interest"
                      ? "Interest only · principal at maturity"
                      : "Principal + interest"}
                  </span>
                </div>
                <dl aria-label="Application loan details">
                  {[
                    ["Property", form.addressShownData],
                    ["Estimated valuation", money(result.value)],
                    [
                      "Property ownership",
                      propertyOwnershipLabel(form.propertyOwnership),
                    ],
                    ...(form.propertyOwnership === "TenancyInCommon"
                      ? [
                          [
                            "Your share of the property",
                            `${form.propertyShare}%`,
                          ],
                        ]
                      : []),
                    ["Your CPF housing usage", money(applicantCpf)],
                    ...(coOwned
                      ? [
                          [
                            "Co-owners' CPF housing usage",
                            money(totalCpf - applicantCpf),
                          ],
                          ["Total CPF housing usage", money(totalCpf)],
                        ]
                      : []),
                    ["Loan type", typesOfLoans[selectedLoanType]?.loanName],
                    ...(selectedLoanType === 2
                      ? [
                          ["Half Caveat borrowing limit", money(halfLimit)],
                          [
                            "Your share of the outstanding mortgage",
                            money(
                              (form.oustandingLoan * form.propertyShare) / 100,
                            ),
                          ],
                        ]
                      : []),
                    [
                      "Payment type",
                      form.loanPaymentType === "interest"
                        ? "Interest only"
                        : "Principal + interest",
                    ],
                    ["Tenure", propertyLoanTenureLabel(selectedLoanType, form)],
                    [
                      "Indicative interest rate",
                      `${(monthlyRate * 100).toFixed(2)}% per month`,
                    ],
                    [
                      "Loan-to-value",
                      selectedLoanType === 2
                        ? `${(((form.loanAmount + applicantCpf + (form.oustandingLoan * form.propertyShare) / 100) / ((result.value * form.propertyShare) / 100)) * 100).toFixed(2)}% of your share (including mortgage and CPF)`
                        : `${(((form.loanAmount + totalCpf + (selectedLoanType === 0 ? 0 : form.oustandingLoan)) / result.value) * 100).toFixed(2)}%`,
                    ],
                    [`Processing fee (${PROPERTY_LOAN_PROCESSING_FEE_LABEL})`, money(propertyLoanProcessingFee(form.loanAmount))],
                    ...(selectedLoanType === 0
                      ? [
                          [
                            "Pays off current bank loan",
                            money(form.oustandingLoan),
                          ],
                        ]
                      : []),
                    [
                      "Estimated cash to you",
                      money(
                        Math.max(
                          0,
                          propertyLoanNetDisbursement(form.loanAmount) -
                            (selectedLoanType === 0 ? form.oustandingLoan : 0),
                        ),
                      ),
                    ],
                    ["CBS report", cbsFiles[0]?.name || "Not attached"],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt>
                        {label}
                        {label === "Estimated cash to you" && (
                          <small>Before legal and valuation fees</small>
                        )}
                      </dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              <PropertyLoanRepaymentSchedule
                schedule={form.loanRepaymentSchedule}
              />
              <label className="pl-confirm">
                <input
                  type="checkbox"
                  checked={submissionConsent}
                  onChange={(event) =>
                    setSubmissionConsent(event.target.checked)
                  }
                />
                I agree that SmartLend may share this application and my CBS
                report with its lending partners so they can make me offers.
              </label>
              <div className="pl-actions">
                <button
                  type="button"
                  className="pl-back"
                  disabled={submitting}
                  onClick={() => navigate(2)}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  className="pl-primary"
                  disabled={
                    !submissionConsent ||
                    submitting ||
                    !loanValid ||
                    !cbsFiles.length
                  }
                  onClick={submit}
                >
                  {submitting ? "Submitting…" : "Submit loan application"}
                </button>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
